import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useMutation, useClient } from 'Data/Api'
import { useDataValue } from 'Simple/Data'
import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { RELATIONSHIPS } from 'Data/constants.js'
import { phoneNumber } from 'Data/validate.js'
import mutation_new_contact_method_association from './newContactMethodAssociationMutation.graphql.js'
import mutationInsertContactMethodAssociation from './mutation-insert-contact-method-association.graphql.js'
import mutation_update_person_relationships from './updatePersonRelationshipsMutation.graphql.js'
import query from './query.graphql.js'
import mutation_send_appointment_reminder from './mutation-send-appointment-reminder.graphql.js'

export default function useDataOnSubmit(props, data) {
  let onActionAddNewRelations = useDataOnActionAddNewRelations(props)
  let onActionNewPhoneForPatient = useDataOnActionNewPhoneForPatient(props)
  let onActionPhoneForPatient = useDataOnActionPhoneForPatient(props)
  let onActionPhoneFromRelative = useDataOnActionPhoneFromRelative(props)
  let onActionFormSubmit = useDataOnActionFormSubmit(props)

  return async function onSubmit({ value, args }) {
    switch (args?.type) {
      case 'add_new_relations': {
        return onActionAddNewRelations({ value, args })
      }
      case 'new_phone_for_patient': {
        return onActionNewPhoneForPatient({ value, args })
      }
      case 'phone_for_patient': {
        return onActionPhoneForPatient({ value, args })
      }
      case 'form_submit': {
        return onActionFormSubmit({ value, args })
      }
      default: {
        onActionPhoneFromRelative({ value, args })
      }
    }
  }
}

function useDataOnActionFormSubmit(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  return async function onAction({ value: phone, args }) {
    if (!phoneNumber(phone.phone.number)) {
      return true
    }
    setFlowTo(normalizePath(props.viewPath, './SearchForPhone/Content'))
  }
}

function useDataOnActionAddNewRelations(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()
  let [, executeMutationUpdatePersonRelationships] = useMutation(
    mutation_update_person_relationships
  )
  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })
  let person_gender = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.gender',
  })
  let patient_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'id',
  })
  return async function onAction({ value, args }) {
    let relations = args.relations.map(rel => ({
      from_person: {
        id: rel.id,
        type: rel.type,
        role: rel.role,
        permitted_to_see_info: rel.role === 'RESPONSIBLE',
        contact_methods: [],
      },
      to_person: {
        id: person_id,
        type: RELATIONSHIPS.find(r => r.id === rel.type)[person_gender],
        role:
          rel.role === 'RESPONSIBLE'
            ? 'DEPENDENT'
            : rel.role === 'DEPENDENT'
            ? 'RESPONSIBLE'
            : 'OTHER',
        permitted_to_see_info: rel.role === 'DEPENDENT',
        contact_methods: [
          {
            dtype: 'phone',
            description: value.description,
            data: {
              number: value.phone.number,
              type: value.phone.type,
            },
            communication_preference: value.appointment_reminders
              ? {
                  patient_id,
                  type: 'APPOINTMENT_MESSAGES',
                }
              : null,
          },
        ],
      },
    }))

    let mutationUpdatePersonRelationshipsResponse =
      await executeMutationUpdatePersonRelationships({
        relationships: relations,
      })
    if (mutationUpdatePersonRelationshipsResponse.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Phone saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionNewPhoneForPatient(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationInsertContactMethodAssociation] = useMutation(
    mutationInsertContactMethodAssociation
  )
  let [, executeSendAppointmentReminder] = useMutation(
    mutation_send_appointment_reminder
  )

  let client = useClient()

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })
  let patient_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'id',
  })
  let patient_uuid = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: '_id',
  })
  return async function onAction({ value }) {
    // if the phone doesn't exist,
    // and we don't want to add a relation,
    // we just save the new phone and add
    // a new association
    let mutationAddPersonContactMethods =
      await executeMutationInsertContactMethodAssociation({
        objects: [
          {
            person_id,
            description: value.description,
            contact_method: {
              dtype: 'phone',
              number: value.phone.number,
              type: value.phone.type,
            },
            communication_preference: value.appointment_reminders
              ? {
                  patient_id,
                  type: 'APPOINTMENT_MESSAGES',
                }
              : null,
          },
        ],
      })

    if (mutationAddPersonContactMethods.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Phone saved!'))
    if (value.send_next_appointment_reminder) {
      let response = await client
        .query(query, {
          patient_id: patient_uuid,
          contact_method_association_id:
            mutationAddPersonContactMethods.data
              .insert_contact_method_associations[0]._id,
        })
        .toPromise()

      if (response.data.vaxiom_appointment_bookings.length === 0) {
        setFlowTo(normalizePath(props.viewPath, '../No'))
        return
      }

      await executeSendAppointmentReminder({
        appointment_booking_id: response.data.vaxiom_appointment_bookings[0].id,
        communication_preference_id:
          response.data.vaxiom_communication_preferences[0].id,
      })
    }

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionPhoneForPatient(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationNewContactMethodAssociation] = useMutation(
    mutation_new_contact_method_association
  )
  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })
  let patient_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'id',
  })
  return async function onAction({ value, args }) {
    // if we found that the phone already exists,
    // but we don't want to add a realtionship,
    // we just link the patient to the existing contact_method
    let mutationNewContactMethodAssociationRespone =
      await executeMutationNewContactMethodAssociation({
        contact_method_id: args.contact_method_id,
        person_id,
        communication_preferences: value.appointment_reminders
          ? {
              data: [
                {
                  patient_id,
                  type: 'APPOINTMENT_MESSAGES',
                },
              ],
            }
          : null,
      })

    if (mutationNewContactMethodAssociationRespone.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Phone saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionPhoneFromRelative(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationNewContactMethodAssociation] = useMutation(
    mutation_new_contact_method_association
  )

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })
  let patient_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'id',
  })
  let patient_uuid = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: '_id',
  })
  let [, executeSendAppointmentReminder] = useMutation(
    mutation_send_appointment_reminder
  )

  let client = useClient()

  return async function onAction({ value: phone }) {
    // if we select a relative's phone we save it
    let mutationNewContactMethodAssociationRespone =
      await executeMutationNewContactMethodAssociation({
        contact_method_id: phone.contact_method_id,
        person_id,
        communication_preferences: phone.appointment_reminders
          ? {
              data: {
                patient_id,
                type: 'APPOINTMENT_MESSAGES',
              },
            }
          : null,
      })

    if (mutationNewContactMethodAssociationRespone.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Phone saved!'))
    if (phone.send_next_appointment_reminder) {
      let response = await client
        .query(query, {
          patient_id: patient_uuid,
          contact_method_association_id:
            mutationNewContactMethodAssociationRespone.data
              .insert_vaxiom_contact_method_associations_one._id,
        })
        .toPromise()

      if (response.data.vaxiom_appointment_bookings.length === 0) {
        setFlowTo(normalizePath(props.viewPath, '../No'))
        return
      }

      await executeSendAppointmentReminder({
        appointment_booking_id: response.data.vaxiom_appointment_bookings[0].id,
        communication_preference_id:
          response.data.vaxiom_communication_preferences[0].id,
      })
    }

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}
